import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
  VpnKey as VpnKeyIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP } from 'components/financepro';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

export const ESTADOS_ECF = [
  { value: '', label: 'Todos' },
  { value: 'aceptado', label: 'Aceptado' },
  { value: 'aceptado_condicional', label: 'Aceptado condicional' },
  { value: 'rechazado', label: 'Rechazado' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'pendiente_envio', label: 'Pendiente de envío' },
  { value: 'firmado', label: 'Firmado' },
  { value: 'error', label: 'Error' },
];

const labelCampoSx = {
  fontWeight: 700,
  fontSize: '0.68rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  mb: 0.4,
};

const valorCampoSx = {
  fontWeight: 600,
  fontSize: '0.9rem',
  color: FP.color.textPrimary,
  wordBreak: 'break-word',
};

export const formatMoneyEcf = (value) =>
  (Number(value) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatFechaEcf = (fecha) => {
  if (!fecha) return '—';
  const s = String(fecha).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  try {
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) return s;
    return date.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return s;
  }
};

export const formatFechaHoraEcf = (fecha) => {
  if (!fecha) return '—';
  const s = String(fecha).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
  return formatFechaEcf(fecha);
};

export const labelTipoEcf = (tipo) => {
  const codigo = String(tipo || '').replace(/\D/g, '');
  return codigo ? `E${codigo}` : '—';
};

export const labelEstadoEcf = (estado) => {
  const key = String(estado || '').toLowerCase();
  const found = ESTADOS_ECF.find((e) => e.value === key);
  if (found && found.value) return found.label;
  if (!key) return '—';
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
};

export const razonesDeFila = (fila) => {
  if (Array.isArray(fila?.razones) && fila.razones.length) return fila.razones;
  return [];
};

export const textoRazonesEcf = (fila) =>
  razonesDeFila(fila)
    .map((r) => [r.codigo, r.mensaje].filter(Boolean).join(' — '))
    .filter(Boolean)
    .join(' | ');

export const ChipEstadoEcf = ({ estado }) => {
  const key = String(estado || '').toLowerCase();
  if (!key) {
    return (
      <Typography sx={{ color: FP.color.textMuted, fontSize: '0.8rem' }}>—</Typography>
    );
  }
  if (key === 'aceptado') {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
        label="Aceptado"
        size="small"
        sx={{
          bgcolor: FP.color.successSoft,
          color: FP.color.successDark,
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          '& .MuiChip-icon': { color: FP.color.successDark },
        }}
      />
    );
  }
  if (key === 'aceptado_condicional') {
    return (
      <Chip
        icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
        label="Condicional"
        size="small"
        sx={{
          bgcolor: FP.color.warningSoft,
          color: '#B45309',
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          '& .MuiChip-icon': { color: '#B45309' },
        }}
      />
    );
  }
  if (key === 'rechazado' || key === 'error') {
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 15 }} />}
        label={key === 'error' ? 'Error' : 'Rechazado'}
        size="small"
        sx={{
          bgcolor: FP.color.dangerSoft,
          color: FP.color.danger,
          fontWeight: 700,
          fontSize: '0.72rem',
          height: 24,
          '& .MuiChip-icon': { color: FP.color.danger },
        }}
      />
    );
  }
  return (
    <Chip
      icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
      label={labelEstadoEcf(key)}
      size="small"
      sx={{
        bgcolor: FP.color.infoSoft,
        color: '#0369A1',
        fontWeight: 700,
        fontSize: '0.72rem',
        height: 24,
        '& .MuiChip-icon': { color: '#0369A1' },
      }}
    />
  );
};

const CampoDetalle = ({ label, value, mono = false }) => (
  <Box>
    <Typography sx={labelCampoSx}>{label}</Typography>
    <Typography
      sx={{
        ...valorCampoSx,
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? '0.85rem' : valorCampoSx.fontSize,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const emitidoDesdeFactura = (factura) => {
  if (!factura) return null;
  const encf = String(factura.encf || factura.ncf || '').trim();
  if (!encf && !factura.nfact) return null;
  return {
    encf,
    nfact: factura.nfact,
    tipofactura: factura.tipofactura,
    fecha_factura: factura.fecha_factura || factura.fecha,
    nombre_cliente: factura.nombre_cliente || factura.cliente_nombre,
    rnc_comprador: factura.rnc_comprador || factura.rnc || factura.suplidor,
    monto_total: factura.monto_total ?? factura.total,
    estado: factura.estado || factura.ecf_estado,
  };
};

/**
 * Dialog reutilizable con el detalle DGII de un e-CF
 * (aceptación, rechazo, condicional, razones, TrackId, firma).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - encf: string (e-NCF). Si no se pasa `emitido`, se consulta al backend.
 *  - emitido: registro ya cargado (p.ej. desde el dashboard). Evita el fetch inicial.
 *  - factura: fila de factura/nota para rellenar identificación si el e-CF no trae join.
 *  - ambiente: ambiente DGII a mostrar si el registro no lo trae.
 *  - onEstadoActualizado: (emitido) => void  tras refrescar contra la DGII.
 */
const DetalleEcf = ({
  open,
  onClose,
  encf = '',
  emitido = null,
  factura = null,
  ambiente = '',
  onEstadoActualizado,
}) => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [detalle, setDetalle] = useState(null);
  const [ambienteLocal, setAmbienteLocal] = useState(ambiente || '');
  const [cargando, setCargando] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');

  const encfConsulta = String(encf || emitido?.encf || factura?.ncf || factura?.encf || '').trim();

  const aplicarDetalle = useCallback((registro, extras = {}) => {
    const base = emitidoDesdeFactura(factura) || {};
    setDetalle({ ...base, ...registro, ...extras });
  }, [factura]);

  const cargarPorEncf = useCallback(async () => {
    if (!encfConsulta || !usuarioLog) return;
    setCargando(true);
    setErrorCarga('');
    try {
      const response = await fetch(servidor + '/api/fiscal_listarecfemitidos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          encf: encfConsulta,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrorCarga(data.Error || 'No se pudo cargar el detalle del e-CF');
        aplicarDetalle({ encf: encfConsulta });
        return;
      }
      if (data.ambiente) setAmbienteLocal(data.ambiente);
      const lista = data.emitidos || [];
      const encontrado =
        lista.find((item) => String(item.encf || '').trim() === encfConsulta) || lista[0];
      if (!encontrado) {
        setErrorCarga('No hay registro de e-CF para este NCF.');
        aplicarDetalle({ encf: encfConsulta });
        return;
      }
      aplicarDetalle(encontrado);
    } catch {
      setErrorCarga('Error de conexión al cargar el detalle del e-CF');
      aplicarDetalle({ encf: encfConsulta });
    } finally {
      setCargando(false);
    }
  }, [encfConsulta, usuarioLog, claveLog, servidor, aplicarDetalle]);

  useEffect(() => {
    if (!open) {
      setDetalle(null);
      setErrorCarga('');
      setCargando(false);
      setRefrescando(false);
      return;
    }
    if (ambiente) setAmbienteLocal(ambiente);
    if (emitido) {
      aplicarDetalle(emitido);
      setErrorCarga('');
    }
  }, [open, emitido, ambiente, aplicarDetalle]);

  useEffect(() => {
    if (!open || emitido || !encfConsulta) return;
    cargarPorEncf();
    // Solo recarga al abrir o cambiar el eNCF; no al actualizar la factura padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, emitido, encfConsulta]);

  const refrescarEstado = async () => {
    if (!encfConsulta || !usuarioLog) return;
    if (!detalle?.track_id) {
      showError('Este comprobante no tiene TrackId para consultar a la DGII');
      return;
    }
    setRefrescando(true);
    try {
      const response = await fetch(servidor + '/api/fiscal_estadoecf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          encf: encfConsulta,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const actualizado = {
          ...detalle,
          estado: data.estado || detalle.estado,
          respuesta: data.respuesta || detalle.respuesta,
          razones: data.razones || detalle.razones,
          track_id: data.track_id || detalle.track_id,
          codigo_seguridad: data.codigo_seguridad || detalle.codigo_seguridad,
          fecha_firma: data.fecha_firma || detalle.fecha_firma,
        };
        setDetalle(actualizado);
        if (typeof onEstadoActualizado === 'function') onEstadoActualizado(actualizado);
        showSuccess(`Estado de ${encfConsulta}: ${labelEstadoEcf(data.estado)}`);
      } else {
        showError(data.Error || 'No se pudo refrescar el estado ante la DGII');
      }
    } catch {
      showError('Error de conexión al refrescar el estado');
    } finally {
      setRefrescando(false);
    }
  };

  // Solo se reenvía lo que nunca llegó a la DGII. Un 'error' puede ser una
  // factura ya anulada en el sistema: ese no se ofrece.
  const puedeReenviar = ['pendiente_envio', 'firmado'].includes(
    String(detalle?.estado || '').toLowerCase().trim()
  );

  const reenviarDgii = async () => {
    if (!encfConsulta || !usuarioLog || !puedeReenviar) return;
    setReenviando(true);
    try {
      const response = await fetch(servidor + '/api/fiscal_reintentarenvios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          rncemisor: emisorFiscal?.rnc || '',
          encfs: [encfConsulta],
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || data.error || 'No se pudo reenviar el e-CF a la DGII');
        return;
      }
      const resultado = (data.procesados || []).find((p) => p.encf === encfConsulta);
      if (!resultado) {
        showError('Este e-CF ya no está pendiente de envío');
        await cargarPorEncf();
        return;
      }
      await cargarPorEncf();
      if (typeof onEstadoActualizado === 'function') {
        onEstadoActualizado({ ...detalle, estado: resultado.estado });
      }
      if (resultado.estado === 'pendiente_envio') {
        showError(`${encfConsulta}: la DGII sigue sin responder, intente más tarde`);
      } else {
        showSuccess(`${encfConsulta}: ${labelEstadoEcf(resultado.estado)}`);
      }
    } catch {
      showError('Error de conexión al reenviar el e-CF');
    } finally {
      setReenviando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          maxHeight: '85vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VpnKeyIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Detalle del e-CF
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontFamily: 'monospace' }}>
            {detalle?.encf || encfConsulta || '—'}
          </Typography>
        </Box>
        <ChipEstadoEcf estado={detalle?.estado} />
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {errorCarga ? (
              <Typography sx={{ color: FP.color.danger, fontSize: '0.88rem', fontWeight: 600 }}>
                {errorCarga}
              </Typography>
            ) : null}

            <Box>
              <Typography sx={{ ...labelCampoSx, mb: 1.2 }}>Identificación del NCF</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="eNCF" value={detalle?.encf} mono />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CampoDetalle label="Tipo" value={labelTipoEcf(detalle?.tipo_ecf)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CampoDetalle label="Nº factura" value={detalle?.nfact ? String(detalle.nfact) : ''} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="Documento" value={detalle?.tipofactura} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="Fecha factura" value={formatFechaEcf(detalle?.fecha_factura)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="Cliente" value={detalle?.nombre_cliente} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="RNC comprador" value={detalle?.rnc_comprador} mono />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle
                    label="Monto total"
                    value={detalle?.monto_total != null ? `$${formatMoneyEcf(detalle.monto_total)}` : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle
                    label="Vía de envío"
                    value={detalle?.via ? (detalle.via === 'fc' ? 'RFCE (fc)' : 'e-CF completo') : ''}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ ...labelCampoSx, mb: 1.2 }}>Firma y código de seguridad</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle label="Código de seguridad" value={detalle?.codigo_seguridad} mono />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle label="Fecha de firma" value={formatFechaHoraEcf(detalle?.fecha_firma)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle
                    label="XML firmado"
                    value={
                      detalle?.tiene_xml
                        ? detalle.tiene_xml_rfce
                          ? 'e-CF + RFCE'
                          : 'e-CF'
                        : detalle
                          ? 'No disponible'
                          : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle label="Ambiente" value={detalle?.ambiente || ambienteLocal} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle
                    label="Intentos"
                    value={detalle?.intentos != null ? String(detalle.intentos) : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CampoDetalle label="TrackId" value={detalle?.track_id} mono />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="Creado" value={formatFechaHoraEcf(detalle?.creado)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoDetalle label="Última modificación" value={formatFechaHoraEcf(detalle?.fechamod)} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ ...labelCampoSx, mb: 1.2 }}>Razones y notificaciones DGII</Typography>
              {razonesDeFila(detalle).length === 0 ? (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
                  No hay mensajes de rechazo ni observaciones registradas.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {razonesDeFila(detalle).map((razon, idx) => (
                    <Box
                      key={`${razon.codigo}-${idx}`}
                      sx={{
                        px: 1.6,
                        py: 1.2,
                        borderRadius: 1.4,
                        border: `1px solid ${FP.color.dangerSoft}`,
                        bgcolor: '#FFF8F8',
                      }}
                    >
                      {razon.codigo ? (
                        <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: FP.color.danger, mb: 0.3 }}>
                          Código {razon.codigo}
                        </Typography>
                      ) : null}
                      <Typography sx={{ fontSize: '0.88rem', color: FP.color.textPrimary }}>
                        {razon.mensaje}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.6, borderTop: `1px solid ${FP.color.border}` }}>
        {puedeReenviar ? (
          <Button
            variant="contained"
            startIcon={reenviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={reenviando || cargando || !usuarioLog}
            onClick={reenviarDgii}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.accentBlue,
              boxShadow: 'none',
              mr: 'auto',
            }}
          >
            Reenviar a la DGII
          </Button>
        ) : null}
        <Button
          variant="outlined"
          startIcon={refrescando ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={!detalle?.track_id || refrescando || cargando}
          onClick={refrescarEstado}
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
          }}
        >
          Refrescar estado
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleEcf;
